import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Spinner } from '../ui/spinner';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Input } from '../ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Check, CheckCheck, BellRing } from 'lucide-react';
import { notificationAPI } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { SEVERITY_CONFIG, SEVERITY_ORDER } from '../../context/NotificationContext';
import { formatMessage, formatInline } from '../../utils/messageFormatter';
import { PayloadFields, PayloadActions } from './PayloadParts';

// ── Date label helper ──────────────────────────────────────────────────────

function dateLabel(iso) {
  const d = new Date(iso); d.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d.getTime() === today.getTime()) return 'Today';
  if (d.getTime() === yesterday.getTime()) return 'Yesterday';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Notification row ──────────────────────────────────────────────────────────

function NotificationRow({ notification: n, onMarkRead }) {
  const cfg = SEVERITY_CONFIG[n.severity] || SEVERITY_CONFIG.info;
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);

  const fmtTime = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  const preview = n.payload?.message ? formatInline(String(n.payload.message)) : '';

  return (
    <div
      className={`flex gap-0 rounded-[10px] border cursor-pointer overflow-hidden transition-colors ${
        n.is_read
          ? (hovered ? 'bg-surface-2' : 'bg-surface-1')
          : ''
      }`}
      style={n.is_read
        ? { borderColor: 'rgba(255,255,255,.06)' }
        : { background: hovered ? cfg.color + '18' : cfg.color + '0d', borderColor: cfg.color + '40' }
      }
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => setExpanded(e => !e)}
    >
      {/* Severity left strip */}
      <div
        style={{
          width: 4,
          flexShrink: 0,
          borderRadius: '10px 0 0 10px',
          background: cfg.color,
          opacity: n.is_read ? 0.3 : 1,
        }}
      />

      <div className="flex-1 p-3.5 flex gap-3 items-start">
        {/* Unread dot */}
        <div className="shrink-0 pt-1">
          <div
            style={{
              width: 7, height: 7, borderRadius: '50%',
              background: n.is_read ? 'transparent' : cfg.color,
              border: n.is_read ? '1.5px solid rgba(255,255,255,.12)' : 'none',
            }}
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Badges + time */}
          <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
            <span
              className="text-[9px] font-extrabold tracking-[.07em] uppercase px-2 py-0.5 rounded-[5px] text-white leading-none"
              style={{ background: cfg.color }}
            >
              {cfg.label}
            </span>
            {n.responsible && (
              <span className="text-[9px] font-bold tracking-[.05em] uppercase px-2 py-0.5 rounded-[5px] bg-positive/15 text-positive leading-none">
                Responsible
              </span>
            )}
            <span className="text-[9px] font-bold tracking-[.05em] uppercase px-2 py-0.5 rounded-[5px] bg-white/[.07] text-ink-dim leading-none">
              {n.brand?.toUpperCase()}
            </span>
            <span className="text-[10px] text-ink-mute ml-auto shrink-0">{fmtTime(n.created_at)}</span>
          </div>

          {/* Title */}
          {n.payload?.title && (
            <p
              className={`text-[13px] font-bold leading-snug mb-1 tracking-[-0.1px] ${n.is_read ? 'text-ink-mute' : 'text-ink'}`}
            >
              {n.payload.title}
            </p>
          )}

          {/* Preview (collapsed) */}
          {!expanded && preview && (
            <p className="text-xs text-ink-dim leading-relaxed truncate max-w-[600px]">
              {preview}
            </p>
          )}

          {/* Expanded payload */}
          {expanded && (
            <div className="mt-3 pt-3 border-t border-white/[.06]">
              {n.payload?.message && (
                <div className="mb-2.5">{formatMessage(n.payload.message)}</div>
              )}
              <PayloadFields fields={n.payload?.fields} />
              <PayloadActions actions={n.payload?.actions} />
            </div>
          )}
        </div>

        {/* Mark read */}
        {!n.is_read && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={(e) => { e.stopPropagation(); onMarkRead(n.id); }}
                className="shrink-0 p-1.5 rounded-[7px] border border-white/[.08] text-ink-mute hover:text-positive hover:bg-positive/10 hover:border-positive/30 transition-colors"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Mark as read</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

// ── Title filter: dropdown with a search box at the top ──────────────────────

function TitleFilter({ titles, value, onChange }) {
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const filtered = needle
    ? titles.filter((t) => t.toLowerCase().includes(needle))
    : titles;

  return (
    <div className="min-w-[200px]">
      <Select
        value={value || '__all__'}
        onOpenChange={(open) => { if (!open) setSearch(''); }}
        onValueChange={(v) => {
          onChange(v === '__all__' ? '' : v);
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder="All titles" />
        </SelectTrigger>
        <SelectContent>
          {/* stopPropagation keeps keystrokes in the input, out of Radix's typeahead */}
          <div className="sticky top-0 z-10 bg-surface-1 p-1 pb-1.5" onKeyDown={(e) => e.stopPropagation()}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search titles…"
              className="h-8 text-xs"
            />
          </div>
          <SelectItem value="__all__">All titles</SelectItem>
          {filtered.map((t) => (
            <SelectItem key={t} value={t} className="whitespace-normal break-words">
              {t}
            </SelectItem>
          ))}
          {filtered.length === 0 && (
            <p className="px-2 py-3 text-center text-xs text-ink-mute">No titles match</p>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

// ── Push notification banner ──────────────────────────────────────────────

function PushBanner() {
  const { pushSupported, needsInstall, pushEnabled, pushPermission, loading, error, subscribe } = usePushNotifications();

  if (pushEnabled) return null;
  if (!pushSupported && !needsInstall) return null;

  const denied = pushPermission === 'denied';
  const installMode = !pushSupported && needsInstall;

  return (
    <div className="mb-3 rounded-lg border border-brand-blue/30 bg-brand-blue/5 px-4 py-3 flex items-center gap-3">
      <BellRing className="h-4 w-4 text-brand-blue shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-ink">
          {installMode
            ? 'Add to Home Screen for push'
            : denied
              ? 'Push notifications blocked'
              : 'Get push alerts on your phone'}
        </p>
        {error ? (
          <p className="text-[11px] text-negative mt-0.5">{error}</p>
        ) : installMode ? (
          <p className="text-[11px] text-ink-dim">
            On iPhone/iPad, tap the Share button, then &ldquo;Add to Home Screen&rdquo;. Open from your home screen to enable push alerts.
          </p>
        ) : (
          <p className="text-[11px] text-ink-dim">
            {denied
              ? 'Enable notifications in your browser settings to receive push alerts.'
              : 'Enable push notifications to receive alerts even when this tab is closed.'}
          </p>
        )}
      </div>
      {!denied && !installMode && (
        <Button
          variant="outline"
          size="sm"
          onClick={subscribe}
          disabled={loading}
          className="text-xs h-7 gap-1.5 shrink-0"
        >
          {loading ? <Spinner size="sm" /> : 'Enable'}
        </Button>
      )}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────────

export default function NotificationsPage() {
  const { decrementUnread, resetUnread, lastNotification } = useNotifications();

  const [notifications, setNotifications] = useState([]);
  const [total, setTotal]                 = useState(0);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [loading, setLoading]             = useState(true);
  const [page, setPage]                   = useState(1);
  const [brands, setBrands]               = useState([]);
  const [titles, setTitles]               = useState([]);

  const [filterBrand, setFilterBrand]     = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterUnread, setFilterUnread]   = useState(false);
  const [filterTitle, setFilterTitle]     = useState('');
  const [filterResponsible, setFilterResponsible] = useState('');

  const PAGE_SIZE = 25;

  const fetchNotifications = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: PAGE_SIZE };
      if (filterBrand)       params.brand = filterBrand;
      if (filterSeverity)    params.severity = filterSeverity;
      if (filterUnread)      params.unread_only = true;
      if (filterTitle)       params.title = filterTitle;
      if (filterResponsible) params.responsible = filterResponsible;

      const res = await notificationAPI.getList(params);
      setNotifications(res.data.notifications || []);
      setTotal(res.data.total || 0);
      setUnreadCount(res.data.unread_count || 0);
      setPage(p);
    } catch {}
    finally { setLoading(false); }
  }, [filterBrand, filterSeverity, filterUnread, filterTitle, filterResponsible]);

  useEffect(() => { fetchNotifications(1); }, [fetchNotifications]);

  useEffect(() => {
    notificationAPI.getBrands().then(res => setBrands(res.data.brands || [])).catch(() => {});
  }, []);

  const loadTitles = useCallback(() => {
    notificationAPI.getTitles().then(res => setTitles(res.data.titles || [])).catch(() => {});
  }, []);

  useEffect(() => { loadTitles(); }, [loadTitles]);

  useEffect(() => {
    if (!lastNotification) return;
    fetchNotifications(page);
    loadTitles();
  }, [lastNotification]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMarkRead = async (id) => {
    try {
      await notificationAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
      decrementUnread(1);
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllRead(filterBrand ? { brand: filterBrand } : {});
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      resetUnread();
    } catch {}
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="max-w-[860px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-ink">Notifications</h2>
          {unreadCount > 0 && (
            <p className="text-xs text-ink-dim mt-0.5">{unreadCount} unread</p>
          )}
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" size="sm" onClick={handleMarkAllRead} className="gap-1.5 text-xs">
            <CheckCheck className="h-3.5 w-3.5" />
            Mark all as read
          </Button>
        )}
      </div>

      <PushBanner />

      <div className="flex gap-2 mb-3 flex-wrap items-center">
        <div className="min-w-[140px]">
          <Select value={filterBrand || '__all__'} onValueChange={(v) => setFilterBrand(v === '__all__' ? '' : v)}>
            <SelectTrigger>
              <SelectValue placeholder="All brands" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All brands</SelectItem>
              {brands.map(b => (
                <SelectItem key={b} value={b}>{b.charAt(0).toUpperCase() + b.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-[140px]">
          <Select value={filterSeverity || '__all__'} onValueChange={(v) => setFilterSeverity(v === '__all__' ? '' : v)}>
            <SelectTrigger>
              <SelectValue placeholder="All severities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All severities</SelectItem>
              {SEVERITY_ORDER.map(s => (
                <SelectItem key={s} value={s}>
                  <span className="capitalize">{SEVERITY_CONFIG[s]?.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-[150px]">
          <Select value={filterResponsible || '__all__'} onValueChange={(v) => setFilterResponsible(v === '__all__' ? '' : v)}>
            <SelectTrigger>
              <SelectValue placeholder="All messages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All messages</SelectItem>
              <SelectItem value="true">I'm responsible</SelectItem>
              <SelectItem value="false">Not responsible</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <TitleFilter titles={titles} value={filterTitle} onChange={setFilterTitle} />

        <button
          onClick={() => setFilterUnread(v => !v)}
          className={`px-3 py-1.5 text-[11px] font-bold rounded-[8px] border cursor-pointer transition-colors ${
            filterUnread
              ? 'bg-warning/10 border-warning/40 text-warning'
              : 'border-white/10 text-ink-mute hover:text-ink-dim hover:border-white/20'
          }`}
        >
          Unread only
        </button>
      </div>

      <div className="bg-surface-1 border border-white/10 rounded-xl overflow-hidden p-1.5 flex flex-col gap-1.5">
        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner size="sm" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-8 text-center text-sm text-ink-dim">No notifications</div>
        ) : (
          notifications.map((n, i) => {
            const label = dateLabel(n.created_at);
            const prevLabel = i > 0 ? dateLabel(notifications[i - 1].created_at) : null;
            const showDate = label !== prevLabel;
            return (
              <React.Fragment key={n.id}>
                {showDate && (
                  <div className="px-1 pt-2 pb-1">
                    <span className="text-[10px] font-bold text-ink-mute uppercase tracking-[.08em]">
                      {label}
                    </span>
                  </div>
                )}
                <NotificationRow notification={n} onMarkRead={handleMarkRead} />
              </React.Fragment>
            );
          })
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => fetchNotifications(page - 1)}>
            Previous
          </Button>
          <span className="text-xs text-ink-dim self-center">{page} / {totalPages}</span>
          <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => fetchNotifications(page + 1)}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
